import React, { useRef } from 'react';

/**
 * InvoiceView Component
 * Displays invoice in the exact same layout as the PDF
 * This ensures pixel-accurate matching between CRM view and PDF output
 */
const InvoiceView = ({ invoice, onClose, onDownloadPDF, backendUrl }) => {
  const invoiceRef = useRef(null);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toFixed(2)}`;
  };

  // Station details (should match PDF exactly)
  const stationDetails = {
    name: 'KIOO RADIO 98.1 FM',
    address: 'Betche Hill, Foya, Lofa County',
    country: 'Liberia, West Africa',
    phones: '+231 887 981 981 / +231 777 981 981',
    email: 'admin@proudlyliberian.com',
    website: 'www.kiooradio.org',
    tagline: 'The Gift of Good News'
  };

  // Payment instructions based on client type (international by default)
  const getPaymentInstructions = () => {
    const clientType = invoice.client_type || 'international';
    
    if (clientType === 'local') {
      return `Payment can be made via Mobile Money or bank transfer. For Mobile Money, send to: Orange Money: +231 XXX XXX XXX (Kioo Radio). For questions: ${stationDetails.email}`;
    }
    
    // International clients
    return `Please make payment by the due date. For questions: ${stationDetails.email}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
        {/* Action Bar */}
        <div className="sticky top-0 bg-gray-100 px-6 py-3 flex justify-between items-center border-b">
          <h2 className="font-semibold text-gray-800">Invoice Preview</h2>
          <div className="flex gap-3">
            <button
              onClick={() => onDownloadPDF(invoice.invoice_id, invoice.invoice_number)}
              className="px-4 py-2 bg-kioo-primary text-white rounded-lg hover:bg-kioo-secondary transition-colors text-sm font-medium"
            >
              📥 Download PDF
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
            >
              ✕ Close
            </button>
          </div>
        </div>

        {/* Invoice Content - Matches PDF Layout Exactly */}
        <div ref={invoiceRef} className="bg-white" style={{ fontFamily: 'Arial, sans-serif' }}>
          
          {/* Top Green Band */}
          <div className="h-5 bg-[#059669]"></div>

          {/* Header Section */}
          <div className="px-12 pt-6 pb-4 flex justify-between items-start">
            {/* Logo - Left */}
            <div>
              <img 
                src="/logo.png" 
                alt="Kioo Radio" 
                className="h-14 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="hidden text-[#059669] font-bold text-xl">
                KIOO RADIO
              </div>
            </div>

            {/* Station Details - Right */}
            <div className="text-right">
              <h1 className="text-lg font-bold text-gray-800">{stationDetails.name}</h1>
              <p className="text-sm text-gray-500">{stationDetails.address}</p>
              <p className="text-sm text-gray-500">{stationDetails.country}</p>
              <p className="text-sm text-gray-500">{stationDetails.phones}</p>
              <p className="text-sm text-gray-500">{stationDetails.email}</p>
            </div>
          </div>

          {/* Invoice Title Banner */}
          <div className="mx-12 mb-6">
            <div className="bg-[#059669] rounded-md py-2 text-center">
              <h2 className="text-2xl font-bold text-white tracking-wide">INVOICE</h2>
            </div>
          </div>

          {/* Bill To & Invoice Details Cards */}
          <div className="mx-12 mb-6 grid grid-cols-2 gap-4">
            {/* Bill To Card */}
            <div className="bg-[#f0fdf4] rounded-md p-4">
              <h3 className="text-sm font-bold text-[#059669] mb-2">BILL TO</h3>
              <p className="font-bold text-gray-800">{invoice.ministry_name}</p>
              <p className="text-sm text-gray-500">
                {invoice.billing_emails?.[0] || ''}
              </p>
            </div>

            {/* Invoice Details Card */}
            <div className="bg-[#f0fdf4] rounded-md p-4">
              <h3 className="text-sm font-bold text-[#059669] mb-2">INVOICE DETAILS</h3>
              <div className="space-y-1 text-sm">
                <p><span className="text-gray-600">Invoice #:</span> <span className="font-medium">{invoice.invoice_number}</span></p>
                <p><span className="text-gray-600">Date:</span> <span className="font-medium">{formatDate(invoice.invoice_date)}</span></p>
                <p><span className="text-gray-600">Due Date:</span> <span className="font-medium">{formatDate(invoice.due_date)}</span></p>
              </div>
            </div>
          </div>

          {/* Line Items Table */}
          <div className="mx-12 mb-6">
            {/* Table Header */}
            <div className="bg-[#059669] rounded-t-md px-4 py-2 grid grid-cols-12 gap-2 text-white font-bold text-sm">
              <div className="col-span-6">DESCRIPTION</div>
              <div className="col-span-2 text-center">QTY</div>
              <div className="col-span-2 text-center">RATE</div>
              <div className="col-span-2 text-right">AMOUNT</div>
            </div>

            {/* Table Body */}
            <div className="border border-gray-200 border-t-0 rounded-b-md">
              {(invoice.line_items || []).map((item, idx) => (
                <div 
                  key={idx} 
                  className={`px-4 py-3 grid grid-cols-12 gap-2 text-sm ${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
                >
                  <div className="col-span-6 text-gray-800">{item.description}</div>
                  <div className="col-span-2 text-center text-gray-800">{item.quantity}</div>
                  <div className="col-span-2 text-center text-gray-800">{formatCurrency(item.rate)}</div>
                  <div className="col-span-2 text-right font-semibold text-gray-800">{formatCurrency(item.amount)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals Section */}
          <div className="mx-12 mb-6 flex justify-end">
            <div className="w-64">
              <div className="flex justify-between py-1 text-sm">
                <span className="text-gray-500">Subtotal:</span>
                <span className="text-gray-800">{formatCurrency(invoice.subtotal)}</span>
              </div>
              <div className="flex justify-between py-1 text-sm">
                <span className="text-gray-500">Tax:</span>
                <span className="text-gray-800">{formatCurrency(invoice.tax)}</span>
              </div>
              {/* Total Due - Green Pill */}
              <div className="mt-2 bg-[#059669] rounded-md px-4 py-2 flex justify-between items-center">
                <span className="text-white font-bold">TOTAL DUE:</span>
                <span className="text-white font-bold text-lg">
                  {formatCurrency(invoice.total)} {invoice.currency || 'USD'}
                </span>
              </div>
            </div>
          </div>

          {/* Divider with Heart */}
          <div className="mx-12 mb-4">
            <div className="border-t-2 border-[#10b981] relative">
              <div className="absolute left-1/2 -translate-x-1/2 -top-3 bg-white px-2">
                <span className="text-2xl">💚</span>
              </div>
            </div>
          </div>

          {/* Appreciation Message */}
          <div className="mx-12 mb-6 text-center">
            <p className="text-gray-600 italic text-sm leading-relaxed max-w-2xl mx-auto">
              {invoice.appreciation_message || 
                `Thank you for partnering with Kioo Radio to bring the gift of good news to the Makona River Region! Your support helps us broadcast faith, hope, and love across Liberia, Sierra Leone, and Guinea.`
              }
            </p>
          </div>

          {/* Payment Instructions Box */}
          <div className="mx-12 mb-8">
            <div className="bg-[#fef3c7] border border-[#f59e0b] rounded-md p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">💳</span>
                <h3 className="font-bold text-[#92400e]">Payment Instructions</h3>
              </div>
              <p className="text-sm text-[#78350f]">
                {getPaymentInstructions()}
              </p>
            </div>
          </div>

          {/* Footer Bar */}
          <div className="bg-[#059669] py-3 text-center text-white">
            <p className="text-xs">
              {stationDetails.name} • Broadcasting Faith, Hope & Love • Liberia • Sierra Leone • Guinea
            </p>
            <p className="text-xs mt-1">
              {stationDetails.website} • {stationDetails.tagline}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceView;
